import React, { useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import axios from "axios";
import * as Yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";

export default function ContactUs() {
  const recaptchaRef = useRef();
  const [captchaToken, setCaptchaToken] = useState("");
  const siteKey = process.env.REACT_APP_GOOGLE_ID;

  const validationSchema = Yup.object().shape({
    name: Yup.string().min(2).max(50).required("Name is required"),
    email: Yup.string().email().required("Email is required"),
    phone: Yup.string()
      .matches(/^\d{3}-\d{3}-\d{4}$/, "Phone must be in format 999-999-9999")
      .required("Phone is required"),
    service: Yup.string().required("Please select a service"),
    message: Yup.string().min(10).required("Message is required"),
  });

  const handleSubmit = async (values, { resetForm }) => {
    try {
      const res = await fetch(
        "https://mbmailer.mb-techs.com/mbmailer/form",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_MBMAILER_KEY,
          },
          body: JSON.stringify({
            name: values.name,
            appName: "Pure Business Solutions Contact Form",
            business: "Pure Business Solutions",
            email: process.env.REACT_APP_MBMAILER_EMAIL,
            to: "mauriceandkimjohnson42@gmail.com",
            subject: `New ${values.service} Inquiry`,
            message: `
  Name: ${values.name}

  Email: ${values.email}

  Phone: ${values.phone}

  Service: ${values.service}

  --------------------------------

  ${values.message}
            `,
          }),
        }
      );

      const result = await res.json();

      if (!res.ok) {
        throw new Error(result.error || result.message || "Failed to send.");
      }

      alert("Message sent!");

      resetForm();
      setCaptchaToken("");
      recaptchaRef.current?.reset();
    } catch (err) {
      console.error(err);
      alert(err.message || "Unable to send your message.");
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#3E2C20] via-[#5A4332] to-[#A97458] min-h-screen text-white font-sans py-12 px-4">
      <div className="max-w-3xl mx-auto text-center mb-10">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4">Contact Us</h1>
        <p className="text-lg md:text-xl text-accentyellow font-semibold">
          We'd love to hear from you!
        </p>
      </div>

      <Formik
        initialValues={{ name: "", email: "", phone: "", message: "", service:"" }}
        validationSchema={validationSchema}
        onSubmit={(values, helpers) => {
            handleSubmit(values, helpers);
        }}
      >
        {({ errors, touched }) => (
          <Form className="max-w-2xl mx-auto bg-white text-black p-8 rounded-xl shadow-xl space-y-6">
            <input type="hidden" name="_next" value="https://pbizs.com/thank-you" />
            <input type="hidden" name="_captcha" value="false" />

            {/* Name */}
            <div>
              <label className="block mb-2 font-semibold text-gray-700">Full Name</label>
              <Field
                name="name"
                className={`w-full px-4 py-3 rounded-md border ${
                  errors.name && touched.name ? "border-red-500" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-green-400`}
              />
              <ErrorMessage name="name" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            {/* Email */}
            <div>
              <label className="block mb-2 font-semibold text-gray-700">Email Address</label>
              <Field
                name="email"
                type="email"
                className={`w-full px-4 py-3 rounded-md border ${
                  errors.email && touched.email ? "border-red-500" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-green-400`}
              />
              <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            {/* Phone (no masking) */}
            <div>
              <label className="block mb-2 font-semibold text-gray-700">Phone Number</label>
              <Field
                name="phone"
                type="text"
                placeholder="999-999-9999"
                className={`w-full px-4 py-3 rounded-md border ${
                  errors.phone && touched.phone ? "border-red-500" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-green-400`}
              />
              <ErrorMessage name="phone" component="div" className="text-red-500 text-sm mt-1" />
            </div>
            <div>
              <label className="block mb-2 font-semibold text-gray-700">Service</label>
              <Field
                name="service"
                as="select"
                className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                <option value="">-- Select a Service --</option>
                <option value="Coaching">Coaching</option>
                <option value="Virtual Assistance">Virtual Assistance</option>
                <option value="Event Planning">Event Planning</option>
                <option value="Training">Training</option>
                <option value="Other">Other</option>
              </Field>
              <ErrorMessage name="service" component="div" className="text-red-500 text-sm mt-1" />
            </div>
            {/* Message */}
            <div>
              <label className="block mb-2 font-semibold text-gray-700">Message</label>
              <Field
                name="message"
                as="textarea"
                rows="5"
                className={`w-full px-4 py-3 rounded-md border ${
                  errors.message && touched.message ? "border-red-500" : "border-gray-300"
                } focus:outline-none focus:ring-2 focus:ring-green-400`}
              />
              <ErrorMessage name="message" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            {/* reCAPTCHA */}
            <div className="pt-2">
              <ReCAPTCHA ref={recaptchaRef} sitekey={siteKey} onChange={setCaptchaToken} />
            </div>

            <button
              type="submit"
              className="bg-yellow-400 text-green-900 font-bold py-3 px-6 rounded-md text-lg shadow-md w-full mt-4"
            >
              Send Message
            </button>
          </Form>
        )}
      </Formik>

      {/* Hidden real form */}
      <form
        id="realForm"
        action="https://formsubmit.co/229b53fcb12bae177ed7f28299507953"
        method="POST"
        className="hidden"
      >
        <input type="hidden" name="_next" value="https://pbizs.com/thank-you" />
        <input type="hidden" name="_captcha" value="false" />
        <input type="hidden" name="name" />
        <input type="hidden" name="email" />
        <input type="hidden" name="phone" />
        <input type="hidden" name="service" />
        <input type="hidden" name="message" />
        <input type="hidden" name="g-recaptcha-response" />
      </form>
    </div>
  );
}
